/**
 * Decode, lookup, scroll, and focus for URL hash targets.
 * Do not use SearchContext.scrollToElement: hash alignment is `block: 'start'`;
 * local search stays `center`.
 */

export const HASH_SCROLL_INTO_VIEW_OPTIONS: ScrollIntoViewOptions = {
    block: 'start',
    inline: 'nearest',
};

const FILE_OUTLET_SOURCE = 'file';
const BOOK_OUTLET_SOURCE = 'book';

/**
 * Strip a leading `#`, then `decodeURIComponent`.
 * Empty hash or invalid encoding → `null` (treat as a missing id).
 */
export function decodeHashFragment(hash: string): string | null {
    if (!hash) {
        return null;
    }
    const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!stripped) {
        return null;
    }
    try {
        return decodeURIComponent(stripped);
    } catch {
        return null;
    }
}

/**
 * Nearest ancestor `div.__outlet[data-source]` (includes `el` itself).
 * Nested book-inside-file is owned by `book`, not `file`.
 */
export function getOwningOutletSource(el: Element): string | null {
    const outlet = el.closest('div.__outlet[data-source]');
    return outlet?.getAttribute('data-source') ?? null;
}

function firstOwnedBySource(id: string, source: string, root: Document): HTMLElement | null {
    const matches = root.querySelectorAll('[id]');
    for (let i = 0; i < matches.length; i++) {
        const el = matches[i];
        if (!(el instanceof HTMLElement) || el.id !== id) {
            continue;
        }
        if (getOwningOutletSource(el) === source) {
            return el;
        }
    }
    return null;
}

/**
 * Lookup order: first id owned by a file `.__outlet[data-source="file"]`,
 * then book outlet, then `document.getElementById` (layout chrome is valid).
 * Ownership is the nearest ancestor outlet with `data-source`.
 */
export function findHashTargetElement(id: string, root: Document = document): HTMLElement | null {
    if (!id) {
        return null;
    }
    const fileMatch = firstOwnedBySource(id, FILE_OUTLET_SOURCE, root);
    if (fileMatch) {
        return fileMatch;
    }
    const bookMatch = firstOwnedBySource(id, BOOK_OUTLET_SOURCE, root);
    if (bookMatch) {
        return bookMatch;
    }
    const byId = root.getElementById(id);
    return byId instanceof HTMLElement ? byId : null;
}

export function scrollHashTargetIntoView(element: HTMLElement | null): void {
    element?.scrollIntoView?.(HASH_SCROLL_INTO_VIEW_OPTIONS);
}

function isAlreadyFocusable(element: HTMLElement): boolean {
    if (element.hasAttribute('tabindex')) {
        return true;
    }
    return element.tabIndex >= 0;
}

/**
 * Focus the target if it is already focusable; otherwise set `tabindex="-1"` and focus.
 * Uses `preventScroll` so focus does not fight `scrollIntoView({ block: 'start' })`.
 */
export function focusHashTarget(element: HTMLElement | null): void {
    if (!element) {
        return;
    }
    if (!isAlreadyFocusable(element)) {
        element.setAttribute('tabindex', '-1');
    }
    element.focus({ preventScroll: true });
}
